import { announceColorChanged, changeColor } from './strings.locstring.json';
import type { AssetId, ProjectId } from 'owa-timestream-types';
import { ControlIcons } from 'owa-control-icons';
import { getAsset, setColor, setAnnouncement } from 'owa-timestream-store';
import { getIsDarkTheme } from 'owa-fabric-theme';
import { getPalette } from 'owa-theme';
import type { IContextualMenuItem } from '@fluentui/react/lib/ContextualMenu';
import colorMap from './colorMap';
import loc from 'owa-localize';

export default function changeColorCommand(
    projectId: ProjectId,
    selectedAssets: AssetId[],
    getAssetDescriptor: (type: string) => any
) {
    const isLightTheme = !getIsDarkTheme();
    const selectedColor = getSelectedColor(projectId, selectedAssets, getAssetDescriptor);
    const ringIconColor = getPalette().neutralTertiary;
    return {
        id: 'openColorPickerContextualMenu',
        iconName: ControlIcons.Color,
        titleResourceId: changeColor,
        getMenuItems: () =>
            colorMap.map(({ color, textKey, styleColor, showRing }) => {
                const useRingIcon = isLightTheme && !!showRing;
                return {
                    key: color,
                    text: loc(textKey),
                    canCheck: true,
                    checked: selectedColor === color,
                    onClick: onItemClicked,
                    data: { projectId, selectedAssets, color, getAssetDescriptor },
                    iconProps: {
                        iconName: useRingIcon ? ControlIcons.CircleRing : ControlIcons.CircleFill,
                    },
                    itemProps: {
                        styles: {
                            root: { outlineOffset: '-1px' },
                            icon: {
                                color: useRingIcon ? ringIconColor : styleColor,
                            },
                        },
                    },
                };
            }),
    };
}

function getSelectedColor(
    projectId: ProjectId,
    selectedAssets: AssetId[],
    getAssetDescriptor?: (type: string) => any
) {
    // TODO: deal with notes and multi-selection
    if (selectedAssets.length == 1) {
        const { color, payload, type } = getAsset(projectId, selectedAssets[0]);
        const assetDesc = getAssetDescriptor?.(type);
        return assetDesc?.getBackgroundColor?.(payload) || color;
    }

    return null;
}

function onItemClicked(
    _?: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>,
    item?: IContextualMenuItem
) {
    if (item) {
        setAnnouncement(loc(announceColorChanged, item.text));
        const { projectId, selectedAssets, color, getAssetDescriptor } = item.data as {
            projectId: ProjectId;
            selectedAssets: AssetId[];
            color: string;
            getAssetDescriptor: (type: string) => any;
        };
        for (const assetId of selectedAssets) {
            const asset = getAsset(projectId, assetId);
            setColor(projectId, assetId, color);
            getAssetDescriptor(asset.type)?.onSetColor?.(color, asset.payload);
        }
    }
}
